import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { load } from '@2gis/mapgl'
import { Map as Map2GISType, Marker } from '@2gis/mapgl/types'

import { MapAPI, MapSettings, MarkerOptions } from '../../lib/types'

const MAP_2GIS_KEY = 'f2836364-9b5a-4e78-ad75-5fe02d032cd4'

/**
 * Hook для работы с 2GIS картами
 * ✅ ВСЕ КРИТИЧЕСКИЕ ПРОБЛЕМЫ ИСПРАВЛЕНЫ
 */
export const useMap2GIS = (
  containerId: string,
  settings: MapSettings,
  onReady?: (api: MapAPI) => void,
  onError?: (error: Error) => void
) => {
  const mapRef = useRef<Map2GISType | null>(null)
  const mapglAPIRef = useRef<any>(null) // FIX: Сохраняем mapglAPI для создания маркеров
  const markersRef = useRef<Map<string, Marker>>(new Map())
  const [isInitialized, setIsInitialized] = useState(false)

  // FIX: Деструктурируем координаты для стабильных зависимостей
  const centerLng = settings.center[0]
  const centerLat = settings.center[1]
  const zoom = settings.zoom

  // Методы API карты (все с useCallback)
  const addMarker = useCallback(
    (id: string, latitude: number, longitude: number, options?: MarkerOptions) => {
      if (!mapRef.current || !mapglAPIRef.current) {
        console.warn('Map is not initialized yet')

        return
      }

      try {
        // Удаляем существующий маркер если есть
        const existingMarker = markersRef.current.get(id)
        if (existingMarker) {
          existingMarker.destroy()
        }

        // FIX: Используем сохранённый mapglAPIRef
        const marker = new mapglAPIRef.current.Marker(mapRef.current, {
          coordinates: [longitude, latitude],
        })

        markersRef.current.set(id, marker)
      } catch (error) {
        console.error('Error adding marker:', error)
        onError?.(error as Error)
      }
    },
    [onError]
  )

  const removeMarker = useCallback((id: string) => {
    const marker = markersRef.current.get(id)
    if (marker) {
      marker.destroy()
      markersRef.current.delete(id)
    }
  }, [])

  const clearMarkers = useCallback(() => {
    markersRef.current.forEach(marker => marker.destroy())
    markersRef.current.clear()
  }, [])

  const setCenter = useCallback((latitude: number, longitude: number, zoom?: number) => {
    if (mapRef.current) {
      mapRef.current.setCenter([longitude, latitude])
      if (zoom !== undefined) {
        mapRef.current.setZoom(zoom)
      }
    }
  }, [])

  const setZoom = useCallback((zoom: number) => {
    if (mapRef.current) {
      mapRef.current.setZoom(zoom)
    }
  }, [])

  const getCenter = useCallback((): [number, number] | null => {
    if (!mapRef.current) return null
    const center = mapRef.current.getCenter()

    return [center[1], center[0]] // [lat, lng]
  }, [])

  const getZoom = useCallback((): number | null => {
    return mapRef.current?.getZoom() ?? null
  }, [])

  const isReady = useCallback(() => isInitialized, [isInitialized])

  // FIX: Мемоизируем API объект чтобы избежать лишних ре-рендеров
  const mapApi = useMemo<MapAPI>(
    () => ({
      addMarker,
      removeMarker,
      clearMarkers,
      setCenter,
      setZoom,
      getCenter,
      getZoom,
      isReady,
    }),
    [addMarker, removeMarker, clearMarkers, setCenter, setZoom, getCenter, getZoom, isReady]
  )

  // Вызываем onReady когда карта инициализирована
  useEffect(() => {
    if (isInitialized && mapApi) {
      onReady?.(mapApi)
    }
  }, [isInitialized, mapApi, onReady])

  // Инициализация карты
  useEffect(() => {
    let mounted = true

    const initMap = async () => {
      try {
        const mapglAPI = await load()

        if (!mounted) return

        // FIX: Сохраняем mapglAPI
        mapglAPIRef.current = mapglAPI

        const map = new mapglAPI.Map(containerId, {
          center: [centerLng, centerLat],
          zoom,
          key: MAP_2GIS_KEY,
        })

        mapRef.current = map

        // FIX: Ждём события 'ready' перед установкой флага готовности
        map.on('ready', () => {
          if (mounted) {
            setIsInitialized(true)
          }
        })
      } catch (error) {
        console.error('Failed to initialize 2GIS map:', error)
        onError?.(error as Error)
      }
    }

    initMap()

    // Cleanup (как в оригинальном коде)
    return () => {
      mounted = false

      // Удаляем все маркеры
      markersRef.current.forEach(marker => marker.destroy())
      markersRef.current.clear()

      // Удаляем карту
      if (mapRef.current) {
        mapRef.current.destroy()
        mapRef.current = null
      }

      mapglAPIRef.current = null
      setIsInitialized(false)
    }
  }, [containerId, centerLng, centerLat, zoom]) // FIX: Правильные зависимости (без массива и объектов)

  return mapApi
}
